/**
 * Unit access requests (Story 4) — both halves of the workflow.
 *
 * `RequestAccessInline` is the REQUESTER side: ONE click — the button itself
 * sends the request (viewer level; the owner can upgrade to editor from the
 * members panel after approving). Rendered alongside any 403 from a unit
 * panel or unit action, because the server's deny message points at it.
 * Outcome reports inline: sent / already pending / server error verbatim.
 *
 * `AccessRequestsSection` is the OWNER side: the unit's pending queue with
 * Approve/Deny. The list endpoint is owner-gated, so a 403 simply hides the
 * section (it is the "you are not this unit's owner" signal, not a failure);
 * an empty queue hides it too. Decisions are server-authoritative — approve
 * writes the real grant before the 200, and the hook refetches the queue AND
 * the members panel from the server (never optimistic; see useOrg.ts rule 1).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useAdminUsers } from '@/hooks/useAdminAnalytics';
import { ApiError } from '@/api/client';
import {
  ALREADY_AUTHORIZED_CODE,
  REQUEST_ALREADY_PENDING_CODE,
  type AccessRequest,
} from '@/api/endpoints/accessRequests';
import type { Location } from '@/api/endpoints/org';
import { ErrorState, Panel, SectionHeader, errMessage, isForbidden } from './OrgUi';
import {
  indexUsersById,
  useCreateAccessRequest,
  useDecideAccessRequest,
  useLocationAccessRequests,
} from './useOrg';

// -----------------------------------------------------------------------------
// Requester side
// -----------------------------------------------------------------------------

export function RequestAccessInline({ locationId }: { locationId: string }) {
  const { t } = useTranslation();
  const create = useCreateAccessRequest();

  if (create.isSuccess) {
    return (
      <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
        {t('org.accessRequests.sent')}
      </p>
    );
  }

  // 409 REQUEST_ALREADY_PENDING is a terminal note, not a form error —
  // re-submitting cannot help, the owner already has the request.
  if (create.error instanceof ApiError && create.error.code === REQUEST_ALREADY_PENDING_CODE) {
    return (
      <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
        {t('org.accessRequests.alreadyPending')}
      </p>
    );
  }

  // 409 ALREADY_AUTHORIZED: the server's ACL check says the caller already
  // holds this access — the 403 that surfaced this button came from something
  // else (or has healed). Terminal note, not an error.
  if (create.error instanceof ApiError && create.error.code === ALREADY_AUTHORIZED_CODE) {
    return (
      <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
        {t('org.accessRequests.alreadyAuthorized', 'You already have access.')}
      </p>
    );
  }

  // ONE click: the button itself IS the request (viewer level, no form).
  // An editor upgrade is the owner's call afterwards, from the members panel.
  return (
    <div className="space-y-1">
      <Button
        variant="outline"
        size="sm"
        className="h-11"
        disabled={create.isPending}
        onClick={() => create.mutate({ locationId, accessLevel: 'viewer' })}
      >
        {create.isPending ? <Spinner className="h-4 w-4" /> : t('org.accessRequests.requestAccess')}
      </Button>
      {create.isError && <p className="text-xs text-red-600">{errMessage(create.error)}</p>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Owner side
// -----------------------------------------------------------------------------

export function AccessRequestsSection({ location }: { location: Location }) {
  const { t } = useTranslation();
  const requests = useLocationAccessRequests(location.id);
  const { data: users } = useAdminUsers();
  const userById = useMemo(() => indexUsersById(users), [users]);
  const decide = useDecideAccessRequest();
  const [pending, setPending] = useState<string | null>(null);

  // Hidden while unknown, for non-owners (the owner-gate 403), and when the
  // queue is empty — the section exists only when there is something to
  // decide. Any OTHER failure still surfaces (4-state rule): silently hiding
  // a broken queue would let requests rot unseen.
  const rows = requests.data ?? [];
  if (requests.isLoading) return null;
  if (requests.isError && isForbidden(requests.error)) return null;
  if (!requests.isError && rows.length === 0) return null;

  const act = (r: AccessRequest, decision: 'approve' | 'deny') => {
    setPending(`${r.id}:${decision}`);
    decide.mutate(
      { id: r.id, decision, locationId: location.id },
      { onSettled: () => setPending(null) },
    );
  };

  return (
    <Panel className="p-4">
      <SectionHeader title={t('org.accessRequests.title')} count={rows.length} />
      <div className="mt-3">
        {requests.isError ? (
          <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />
        ) : (
          <div className="space-y-1">
            {rows.map((r) => (
              <div
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-neutral-50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm text-neutral-800">
                      {userById[r.requested_by] ?? r.requested_by}
                    </span>
                    {/* A visibility change carries no level; absent renders
                        as absent rather than as an invented "viewer". */}
                    {r.access_level && (
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-medium capitalize text-neutral-600">
                        {t(`org.accessLevel.${r.access_level}`, r.access_level)}
                      </span>
                    )}
                  </div>
                  {r.justification && (
                    <p className="mt-0.5 truncate text-xs text-neutral-400">{r.justification}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => act(r, 'approve')} disabled={decide.isPending}>
                    {pending === `${r.id}:approve` ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      t('org.accessRequests.approve')
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-neutral-500 hover:bg-red-50 hover:text-red-700"
                    onClick={() => act(r, 'deny')}
                    disabled={decide.isPending}
                  >
                    {pending === `${r.id}:deny` ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      t('org.accessRequests.deny')
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {decide.isError && <p className="mt-2 text-xs text-red-600">{errMessage(decide.error)}</p>}
      </div>
    </Panel>
  );
}
